import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { ColumnDef } from '@3ddv/software-division-components/backoffice/table';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { GsTableComponent } from '../../shared/ui/table/gs-table.component';
import {
  AppDialogService,
  DialogCloseReason,
} from '../layout/dialog/app-dialog.service';
import { PaginationComponent } from '../layout/pagination/pagination.component';
import { CreateComponent } from './modals/create/create.component';
import { DeleteComponent } from './modals/delete/delete.component';
import { EditComponent } from './modals/edit/edit.component';
import { Adlink } from './models/adlink.model';
import { AdlinkService } from './services/adlink.service';
import { AuthStore } from '../../core/stores/auth.store';
import { buildAdlinkColumns } from './adlink.columns';

@Component({
  selector: 'app-adlink',
  templateUrl: './adlink.component.html',
  styleUrls: ['./adlink.component.css'],
  imports: [ButtonComponent, GsTableComponent, PaginationComponent],
})
export class AdlinkComponent implements OnInit, OnDestroy {
  private dialogService = inject(AppDialogService);
  protected readonly authStore = inject(AuthStore);
  private adLinkService = inject(AdlinkService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  public data: Array<Adlink>;
  public totalItemsInDb: number;
  public itemsPerPage: number;
  public currentPage: number;
  public refreshing: boolean;
  public subscriptions: Array<Subscription>;
  public columns: ColumnDef<Adlink>[] = [];
  @ViewChild('pagination') pagination: PaginationComponent;

  constructor() {
    this.data = [];
    this.subscriptions = [];
    this.refreshing = false;
    this.totalItemsInDb = 0;
    this.itemsPerPage = 0;
    this.currentPage = this.activatedRoute.snapshot.queryParams['page'];
  }

  ngOnInit(): void {
    this.columns = buildAdlinkColumns(
      adlink => this.toggleActive(adlink),
      adlink => this.openModalDelete(adlink.id),
      adlink => this.openModalEdit(adlink),
      this.authStore.canEdit()
    );

    let resolved = this.activatedRoute.snapshot.data['api'];
    this.data = resolved.data;
    this.totalItemsInDb = resolved.metadata.total;
    this.itemsPerPage = resolved.metadata.perPage;

    const queryparams$ = this.activatedRoute.queryParams.subscribe(params => {
      let resolved = this.activatedRoute.snapshot.data['api'];

      if (!params['page'] || params['page'] > resolved.metadata.lastPage) {
        this.router.navigate(['.'], {
          relativeTo: this.activatedRoute,
          queryParams: { page: 1 },
        });
      }

      if (params['page']) {
        this.currentPage = params['page'];
        this.data = resolved.data;
        this.totalItemsInDb = resolved.metadata.total;
        this.itemsPerPage = resolved.metadata.perPage;
        if (this.pagination) {
          this.pagination.refresh(
            this.currentPage,
            this.totalItemsInDb,
            this.itemsPerPage
          );
        }
      }
    });
    this.subscriptions.push(queryparams$);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(o => o.unsubscribe());
  }

  public openModalDelete(id) {
    const { closed$ } = this.dialogService.open(DeleteComponent, {
      size: 'md',
      inputs: { id },
    });
    this.refreshListOnSuccess(closed$, 1);
  }

  public openModalEdit(item: Adlink) {
    const { closed$ } = this.dialogService.open(EditComponent, {
      size: 'xl',
      inputs: { item: JSON.parse(JSON.stringify(item)) },
    });
    this.refreshListOnSuccess(closed$, this.currentPage);
  }

  public openModalCreate() {
    const { closed$ } = this.dialogService.open(CreateComponent, {
      size: 'xl',
    });
    this.refreshListOnSuccess(closed$, 1);
  }

  private refreshListOnSuccess(
    closed$: Observable<DialogCloseReason | undefined>,
    page?: string | number
  ) {
    const onClose$ = closed$.subscribe(reason => {
      if (reason === 'success') {
        this.refreshing = true;
        this.getall(page);
      }
    });
    this.subscriptions.push(onClose$);
  }

  private getall(page?) {
    if (!page) page = this.currentPage;

    const all$ = this.adLinkService.getAll$(page).subscribe({
      next: (res: any) => {
        this.refreshing = false;
        this.data = res.data;
        this.totalItemsInDb = res.metadata.total;
        this.itemsPerPage = res.metadata.perPage;
        if (this.pagination) {
          this.pagination.refresh(
            this.currentPage,
            this.totalItemsInDb,
            this.itemsPerPage
          );
        }
        if (this.currentPage > res.metadata.lastPage) {
          this.currentPage = page;
        }
        this.router.navigate(['/private/adlink'], {
          queryParams: { page: this.currentPage },
        });
      },
      error: err => {
        console.log(err);
      },
    });
    this.subscriptions.push(all$);
  }

  public toggleActive(item: Adlink) {
    const updated: Adlink = { ...item, active: !item.active };
    this.data = this.data.map(adlink =>
      adlink.id === item.id ? updated : adlink
    );
    const update$ = this.adLinkService.update$(updated).subscribe({
      next: res => console.log(res),
      error: err => console.log(err),
    });
    this.subscriptions.push(update$);
  }

  public pageChanged($event) {
    this.router.navigate(['/private/adlink'], {
      queryParams: { page: $event.page },
    });
  }
}
